import { Button, ButtonGroup, Card, Heading, IconButton, LuIcon, Separator, Text, toast } from "@overmap-ai/blocks"
import { IssueUpdateChange, OrganizationAccessLevel, type User } from "@overmap-ai/common"
import {
	selectAssetAttachments,
	selectAssetsOfProject,
	selectAssetTypeFieldValues,
	selectAssetTypeIdentifierValues,
	selectAttachmentsOfProject,
	selectCommentsOfAssets,
	selectCommentsOfProject,
	selectFormSubmissionsOfProject,
	selectGeoImagesOfProject,
	selectIssueAssociationsOfIssues,
	selectIssueAttachmentMapping,
	selectIssueCommentMapping,
	selectIssuesOfProject,
	selectIssueTypeFieldValues,
	selectIssueTypeIdentifierValues,
	selectIssueUpdates,
	selectProcedureFieldValues,
	selectProcedureInitiativesOfProject,
	selectProceduresOfProject,
	selectProcedureStepComments,
	selectProcedureStepEvents,
	selectProcedureStepFieldValues,
	selectProcedureStepFieldValuesReviews,
	selectProjectDocuments,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { type FormEventHandler, memo, useCallback, useMemo, useState } from "react"
import type { DateRange } from "react-day-picker"

import { PROJECT_TIMELINE_ITEM_TYPE_GROUPS } from "../../constants"
import { ProjectTimelineItemType, ProjectTimelineItemTypeGroup } from "../../enums/projects"
import { usePagination } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { projectTimelineItemTypeData } from "../../mappings/projects"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS } from "../../tiptap"
import { NewLineFloatingMenu, TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { formatDateRange } from "../../utils/date"
import { ProjectTimelineItemTypeGroupMultiSelectDropdown } from "../DropdownMultiSelects/ProjectTimelineItemTypeGroupMultiSelectDropdown"
import { UserMultiSelect } from "../DropdownMultiSelects/UserMultiSelect"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"
import { DayPickerPopover } from "../Popovers/DayPickerPopover"
import { ProjectCommentTimelineItem } from "./ProjectCommentTimelineItem"
import { ProjectTimelineAssetAttachmentItem } from "./ProjectTimelineAssetAttachmentItem"
import { ProjectTimelineAssetCommentItem } from "./ProjectTimelineAssetCommentItem"
import { ProjectTimelineAssetFieldValuesItem } from "./ProjectTimelineAssetFieldValuesItem"
import { ProjectTimelineAssetIdentifierValueItem } from "./ProjectTimelineAssetIdentifierValueItem"
import { ProjectTimelineAssetItem } from "./ProjectTimelineAssetItem"
import { ProjectTimelineDocumentItem } from "./ProjectTimelineDocumentItem"
import { ProjectTimelineFormSubmissionItem } from "./ProjectTimelineFormSubmissionItem"
import { ProjectTimelineGeoImageItem } from "./ProjectTimelineGeoImageItem"
import { ProjectTimelineIssueAssociationItem } from "./ProjectTimelineIssueAssociationItem"
import { ProjectTimelineIssueAttachmentItem } from "./ProjectTimelineIssueAttachmentItem"
import { ProjectTimelineIssueCommentItem } from "./ProjectTimelineIssueCommentItem"
import { ProjectTimelineIssueFieldValuesItem } from "./ProjectTimelineIssueFieldValuesItem"
import { ProjectTimelineIssueIdentifierValueItem } from "./ProjectTimelineIssueIdentifierValueItem"
import { ProjectTimelineIssueItem } from "./ProjectTimelineIssueItem"
import { ProjectTimelineIssueUpdateItem } from "./ProjectTimelineIssueUpdateItem"
import { ProjectTimelineProcedureFieldValuesItem } from "./ProjectTimelineProcedureFieldValuesItem"
import { ProjectTimelineProcedureInitiativeItem } from "./ProjectTimelineProcedureInitiativeItem"
import { ProjectTimelineProcedureItem } from "./ProjectTimelineProcedureItem"
import { ProjectTimelineProcedureStepCommentItem } from "./ProjectTimelineProcedureStepCommentItem"
import { ProjectTimelineProcedureStepEventItem } from "./ProjectTimelineProcedureStepEventItem"
import { ProjectTimelineProcedureStepFieldValuesItem } from "./ProjectTimelineProcedureStepFieldValuesItem"
import { ProjectTimelineProcedureStepReviewItem } from "./ProjectTimelineProcedureStepReviewItem"
import { ProjectTimelineProjectAttachmentItem } from "./ProjectTimelineProjectAttachmentItem"
import type { ProjectTimelineItem, ProjectTimelineProps } from "./typings"
import { createUserIdList, getTimelineDate } from "./utils"

const ITEMS_PER_PAGE = 100

export const ProjectTimeline = memo<ProjectTimelineProps>((props) => {
	const { projectId } = props

	const assets = useShallowEqaulSelector(selectAssetsOfProject(projectId))
	const issues = useShallowEqaulSelector(selectIssuesOfProject(projectId))
	const procedures = useShallowEqaulSelector(selectProceduresOfProject(projectId))
	const procedureInitiatives = useShallowEqaulSelector(selectProcedureInitiativesOfProject(projectId))
	const documents = useShallowEqaulSelector(selectProjectDocuments(projectId))
	const geoImages = useShallowEqaulSelector(selectGeoImagesOfProject(projectId))
	const projectAttachments = useShallowEqaulSelector(selectAttachmentsOfProject(projectId))
	const projectComments = useShallowEqaulSelector(selectCommentsOfProject(projectId))
	const formSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfProject(projectId))
	const users = useShallowEqaulSelector(selectUsersOfProject(projectId))

	const assetIds = useMemo(() => assets.map(({ uuid }) => uuid), [assets])
	const issueIds = useMemo(() => issues.map(({ uuid }) => uuid), [issues])

	const assetComments = useShallowEqaulSelector(selectCommentsOfAssets(assetIds))
	const issueAssociations = useShallowEqaulSelector(selectIssueAssociationsOfIssues(issueIds))

	const assetAttachments = useShallowEqaulSelector(selectAssetAttachments)
	const assetFieldValues = useShallowEqaulSelector(selectAssetTypeFieldValues)
	const assetIdentifierValues = useShallowEqaulSelector(selectAssetTypeIdentifierValues)
	const issueAttachmentMapping = useAppSelector(selectIssueAttachmentMapping)
	const issueCommentMapping = useAppSelector(selectIssueCommentMapping)
	const issueFieldValues = useShallowEqaulSelector(selectIssueTypeFieldValues)
	const issueIdentifierValues = useShallowEqaulSelector(selectIssueTypeIdentifierValues)
	const issueUpdates = useShallowEqaulSelector(selectIssueUpdates)
	const procedureFieldValues = useShallowEqaulSelector(selectProcedureFieldValues)
	const procedureStepComments = useShallowEqaulSelector(selectProcedureStepComments)
	const procedureStepFieldValues = useShallowEqaulSelector(selectProcedureStepFieldValues)
	const procedureStepEvents = useShallowEqaulSelector(selectProcedureStepEvents)
	const procedureStepReviews = useShallowEqaulSelector(selectProcedureStepFieldValuesReviews)

	const [visibleItemTypes, setVisibleItemTypes] = useState<ProjectTimelineItemTypeGroup[]>(
		PROJECT_TIMELINE_ITEM_TYPE_GROUPS,
	)
	const [dateRange, setDateRange] = useState<DateRange | undefined>()
	const [selectedUserIds, setSelectedUserIds] = useState<User["id"][] | null>(null)

	const selectedUserIdValues = useMemo(() => selectedUserIds ?? users.map(({ id }) => id), [selectedUserIds, users])

	const selectedUserValues = useMemo(() => {
		if (selectedUserIds === null) return users
		const selectedUserIdSet = new Set(selectedUserIds)
		return users.filter(({ id }) => selectedUserIdSet.has(id))
	}, [selectedUserIds, users])

	const editor = useEditor({
		editorProps: {
			attributes: { class: "outline-none" },
		},
		extensions: [
			...BASE_EXTENSIONS,
			...LINK_NODES,
			...MENTION_EXTENSIONS,
			Placeholder.configure({
				placeholder: "Leave a project comment",
			}),
		],
	})

	const timelineItems = useMemo(() => {
		const ret: ProjectTimelineItem[] = []
		const assetIdSet = new Set(assetIds)
		const issueIdSet = new Set(issueIds)
		const procedureIds = procedures.map(({ uuid }) => uuid)
		const procedureIdSet = new Set(procedureIds)
		const procedureStepFieldValueIdSet = new Set<string>()

		for (const asset of assets) {
			ret.push({
				type: ProjectTimelineItemType.ASSET,
				model: asset,
			})
		}

		for (const comment of assetComments) {
			ret.push({
				type: ProjectTimelineItemType.ASSET_COMMENT,
				model: comment,
			})
		}

		for (const attachment of assetAttachments) {
			if (!assetIdSet.has(attachment.asset)) continue

			ret.push({
				type: ProjectTimelineItemType.ASSET_ATTACHMENT,
				model: attachment,
			})
		}

		for (const fieldValues of assetFieldValues) {
			if (!assetIdSet.has(fieldValues.asset)) continue

			ret.push({
				type: ProjectTimelineItemType.ASSET_FIELD_VALUES,
				model: fieldValues,
			})
		}

		for (const identifierValue of assetIdentifierValues) {
			if (!assetIdSet.has(identifierValue.asset)) continue

			ret.push({
				type: ProjectTimelineItemType.ASSET_IDENTIFIER_VALUE,
				model: identifierValue,
			})
		}

		for (const document of documents) {
			ret.push({
				type: ProjectTimelineItemType.DOCUMENT,
				model: document,
			})
		}

		for (const geoImage of geoImages) {
			ret.push({
				type: ProjectTimelineItemType.GEO_IMAGE,
				model: geoImage,
			})
		}

		for (const issueAssociation of issueAssociations) {
			ret.push({
				type: ProjectTimelineItemType.ISSUE_ASSOCIATION,
				model: issueAssociation,
			})
		}

		for (const attachment of Object.values(issueAttachmentMapping)) {
			if (!issueIdSet.has(attachment.issue)) continue

			ret.push({
				type: ProjectTimelineItemType.ISSUE_ATTACHMENT,
				model: attachment,
			})
		}

		for (const comment of Object.values(issueCommentMapping)) {
			if (!issueIdSet.has(comment.issue)) continue

			ret.push({
				type: ProjectTimelineItemType.ISSUE_COMMENT,
				model: comment,
			})
		}

		for (const issue of issues) {
			ret.push({
				type: ProjectTimelineItemType.ISSUE,
				model: issue,
			})
		}

		for (const fieldValues of issueFieldValues) {
			if (!issueIdSet.has(fieldValues.issue)) continue

			ret.push({
				type: ProjectTimelineItemType.ISSUE_FIELD_VALUES,
				model: fieldValues,
			})
		}

		for (const identifierValue of issueIdentifierValues) {
			if (!issueIdSet.has(identifierValue.issue)) continue

			ret.push({
				type: ProjectTimelineItemType.ISSUE_IDENTIFIER_VALUE,
				model: identifierValue,
			})
		}

		for (const issueUpdate of issueUpdates) {
			if (!issueIdSet.has(issueUpdate.issue) || Object.keys(issueUpdate.changes).length === 0) continue

			ret.push({
				type: ProjectTimelineItemType.ISSUE_UPDATE,
				model: issueUpdate,
			})
		}

		for (const formSubmission of formSubmissions) {
			ret.push({
				type: ProjectTimelineItemType.FORM_SUBMISSION,
				model: formSubmission,
			})
		}

		for (const procedureInitiative of procedureInitiatives) {
			ret.push({
				type: ProjectTimelineItemType.PROCEDURE_INITIATIVE,
				model: procedureInitiative,
			})
		}

		for (const fieldValues of procedureFieldValues) {
			if (!procedureIdSet.has(fieldValues.procedure)) continue

			ret.push({
				type: ProjectTimelineItemType.PROCEDURE_FIELD_VALUES,
				model: fieldValues,
			})
		}

		for (const procedure of procedures) {
			if (procedure.procedure_initiative !== null) continue

			ret.push({
				type: ProjectTimelineItemType.PROCEDURE,
				model: procedure,
			})
		}

		for (const comment of procedureStepComments) {
			if (!procedureIdSet.has(comment.procedure)) continue

			ret.push({
				type: ProjectTimelineItemType.PROCEDURE_STEP_COMMENT,
				model: comment,
			})
		}

		for (const fieldValues of procedureStepFieldValues) {
			if (!procedureIdSet.has(fieldValues.procedure)) continue

			procedureStepFieldValueIdSet.add(fieldValues.uuid)

			ret.push({
				type: ProjectTimelineItemType.PROCEDURE_STEP_FIELD_VALUES,
				model: fieldValues,
			})
		}

		for (const event of procedureStepEvents) {
			if (!procedureIdSet.has(event.procedure)) continue

			ret.push({
				type: ProjectTimelineItemType.PROCEDURE_STEP_EVENT,
				model: event,
			})
		}

		for (const review of procedureStepReviews) {
			if (!procedureStepFieldValueIdSet.has(review.field_values)) continue

			const fieldValues = procedureStepFieldValues.find(({ uuid }) => uuid === review.field_values)
			if (!fieldValues) continue

			ret.push({
				type: ProjectTimelineItemType.PROCEDURE_STEP_REVIEW,
				model: review,
			})
		}

		for (const attachment of projectAttachments) {
			ret.push({
				type: ProjectTimelineItemType.PROJECT_ATTACHMENT,
				model: attachment,
			})
		}

		for (const comment of projectComments) {
			ret.push({
				type: ProjectTimelineItemType.PROJECT_COMMENT,
				model: comment,
			})
		}

		return ret
			.toSorted((a, b) => {
				const dateA = getTimelineDate(a.model)
				const dateB = getTimelineDate(b.model)
				if (dateA === dateB) return 0
				return dateA < dateB ? -1 : 1
			})
			.toReversed()
	}, [
		assetAttachments,
		assetComments,
		assetFieldValues,
		assetIdentifierValues,
		assetIds,
		assets,
		documents,
		formSubmissions,
		geoImages,
		issueAssociations,
		issueAttachmentMapping,
		issueCommentMapping,
		issueFieldValues,
		issueIdentifierValues,
		issueIds,
		issues,
		issueUpdates,
		procedureFieldValues,
		procedureInitiatives,
		procedures,
		procedureStepComments,
		procedureStepEvents,
		procedureStepFieldValues,
		procedureStepReviews,
		projectAttachments,
		projectComments,
	])

	const filteredTimelineItems = useMemo(() => {
		const visibleItemTypesSet = new Set(visibleItemTypes)
		const selectedUserIdSet = new Set(selectedUserIdValues)
		const fromTime = dateRange?.from ? dateRange.from.getTime() : null
		const toTime = dateRange?.to ? new Date(dateRange.to).setHours(23, 59, 59, 999) : null

		const getTimelineItemUserIds = (item: ProjectTimelineItem) => {
			switch (item.type) {
				case ProjectTimelineItemType.ISSUE:
					return createUserIdList(item.model.created_by, item.model.assigned_to)
				case ProjectTimelineItemType.ISSUE_UPDATE:
					return createUserIdList(
						item.model.created_by,
						item.model.changes[IssueUpdateChange.ASSIGNED_TO]?.id,
					)
				case ProjectTimelineItemType.PROCEDURE_STEP_EVENT:
					return createUserIdList(item.model.created_by, item.model.value)
				case ProjectTimelineItemType.PROCEDURE_STEP_REVIEW: {
					const fieldValues = procedureStepFieldValues.find(({ uuid }) => uuid === item.model.field_values)
					return createUserIdList(item.model.created_by, fieldValues?.created_by)
				}
				default:
					return createUserIdList(item.model.created_by)
			}
		}

		return timelineItems.filter((entry) => {
			const { group } = projectTimelineItemTypeData[entry.type]

			if (!visibleItemTypesSet.has(group)) return false

			const entryTime = new Date(getTimelineDate(entry.model)).getTime()
			if (fromTime !== null && entryTime < fromTime) return false
			if (toTime !== null && entryTime > toTime) return false

			return selectedUserIds === null || getTimelineItemUserIds(entry).some((id) => selectedUserIdSet.has(id))
		})
	}, [dateRange, procedureStepFieldValues, selectedUserIdValues, selectedUserIds, timelineItems, visibleItemTypes])

	const { page, pageCount, currentPage, nextPage, lastPage, firstPage, prevPage } =
		usePagination<ProjectTimelineItem>(filteredTimelineItems, ITEMS_PER_PAGE)

	const handleClearDateRange = useCallback(() => {
		setDateRange(undefined)
	}, [setDateRange])

	const handleUserValuesChange = useCallback((values: User[]) => {
		setSelectedUserIds(values.map(({ id }) => id))
	}, [])

	const handleCommentSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (!editor || editor.isEmpty) {
				toast.error({
					title: "Comment required",
					description: "Please add a comment before submitting.",
				})
				return
			}

			const [_offlineComment, _promise] = sdk.projectComments.add({
				content: JSON.stringify(editor.getJSON()),
				project: projectId,
				resolved: false,
			})

			editor.commands.clearContent()
		},
		[editor, projectId],
	)

	return (
		<section className="flex w-full flex-col gap-4 overflow-hidden">
			<div className="flex flex-col gap-3">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div className="flex flex-col gap-1">
						<Heading size="xl">Project dashboard</Heading>
						<Text size="sm" accentColor="base">
							{filteredTimelineItems.length} of {timelineItems.length} project timeline items
						</Text>
					</div>

					<ButtonGroup
						variant="soft"
						accentColor="base"
						size="sm"
						className="flex flex-wrap items-center gap-2"
					>
						<ProjectTimelineItemTypeGroupMultiSelectDropdown
							values={visibleItemTypes}
							onValuesChange={setVisibleItemTypes}
							size="sm"
							align="end"
						>
							<Button>
								<LuIcon icon={modelIconMapping.filter} />
								Types
							</Button>
						</ProjectTimelineItemTypeGroupMultiSelectDropdown>

						<DayPickerPopover
							mode="range"
							required={false}
							selected={dateRange}
							onSelect={setDateRange}
							align="end"
							size="sm"
						>
							<Button>
								<LuIcon icon={modelIconMapping.dueDate} />
								{dateRange ? formatDateRange(dateRange) : "Date range"}
							</Button>
						</DayPickerPopover>

						{dateRange ? (
							<IconButton aria-label="clear-date-range" onClick={handleClearDateRange}>
								<LuIcon icon={modelIconMapping.close} />
							</IconButton>
						) : null}

						<UserMultiSelect
							users={users}
							values={selectedUserValues}
							onValuesChange={handleUserValuesChange}
							size="sm"
							variant="soft"
							accentColor="base"
						>
							<Button>
								<LuIcon icon={modelIconMapping.user} />
								People
							</Button>
						</UserMultiSelect>
					</ButtonGroup>
				</div>

				<Separator size="full" />
			</div>

			<div className="flex flex-col gap-3 overflow-hidden">
				<div className="flex w-full flex-col gap-4 overflow-y-auto overflow-x-clip">
					{filteredTimelineItems.length > 0 ? (
						page.map(({ type, model }) => {
							switch (type) {
								case ProjectTimelineItemType.ASSET:
									return <ProjectTimelineAssetItem key={model.uuid} asset={model} />
								case ProjectTimelineItemType.ASSET_COMMENT:
									return <ProjectTimelineAssetCommentItem key={model.uuid} assetComment={model} />
								case ProjectTimelineItemType.ASSET_ATTACHMENT:
									return (
										<ProjectTimelineAssetAttachmentItem key={model.uuid} assetAttachment={model} />
									)
								case ProjectTimelineItemType.ASSET_FIELD_VALUES:
									return (
										<ProjectTimelineAssetFieldValuesItem
											key={model.uuid}
											assetTypeFieldValues={model}
										/>
									)
								case ProjectTimelineItemType.ASSET_IDENTIFIER_VALUE:
									return (
										<ProjectTimelineAssetIdentifierValueItem
											key={model.uuid}
											assetTypeIdentifierValue={model}
										/>
									)
								case ProjectTimelineItemType.DOCUMENT:
									return <ProjectTimelineDocumentItem key={model.uuid} document={model} />
								case ProjectTimelineItemType.GEO_IMAGE:
									return <ProjectTimelineGeoImageItem key={model.uuid} geoImage={model} />
								case ProjectTimelineItemType.ISSUE_ASSOCIATION:
									return (
										<ProjectTimelineIssueAssociationItem
											key={model.uuid}
											issueAssociation={model}
										/>
									)
								case ProjectTimelineItemType.ISSUE_ATTACHMENT:
									return (
										<ProjectTimelineIssueAttachmentItem key={model.uuid} issueAttachment={model} />
									)
								case ProjectTimelineItemType.ISSUE_COMMENT:
									return <ProjectTimelineIssueCommentItem key={model.uuid} issueComment={model} />
								case ProjectTimelineItemType.ISSUE:
									return <ProjectTimelineIssueItem key={model.uuid} issue={model} />
								case ProjectTimelineItemType.ISSUE_FIELD_VALUES:
									return (
										<ProjectTimelineIssueFieldValuesItem
											key={model.uuid}
											issueTypeFieldValues={model}
										/>
									)
								case ProjectTimelineItemType.ISSUE_IDENTIFIER_VALUE:
									return (
										<ProjectTimelineIssueIdentifierValueItem
											key={model.uuid}
											issueTypeIdentifierValue={model}
										/>
									)
								case ProjectTimelineItemType.ISSUE_UPDATE:
									return <ProjectTimelineIssueUpdateItem key={model.uuid} issueUpdate={model} />
								case ProjectTimelineItemType.FORM_SUBMISSION:
									return <ProjectTimelineFormSubmissionItem key={model.uuid} formSubmission={model} />
								case ProjectTimelineItemType.PROCEDURE_INITIATIVE:
									return (
										<ProjectTimelineProcedureInitiativeItem
											key={model.uuid}
											procedureInitiative={model}
										/>
									)
								case ProjectTimelineItemType.PROCEDURE_FIELD_VALUES:
									return (
										<ProjectTimelineProcedureFieldValuesItem
											key={model.uuid}
											procedureFieldValues={model}
										/>
									)
								case ProjectTimelineItemType.PROCEDURE:
									return <ProjectTimelineProcedureItem key={model.uuid} procedure={model} />
								case ProjectTimelineItemType.PROCEDURE_STEP_COMMENT:
									return (
										<ProjectTimelineProcedureStepCommentItem
											key={model.uuid}
											procedureStepComment={model}
										/>
									)
								case ProjectTimelineItemType.PROCEDURE_STEP_FIELD_VALUES:
									return (
										<ProjectTimelineProcedureStepFieldValuesItem
											key={model.uuid}
											procedureStepFieldValues={model}
										/>
									)
								case ProjectTimelineItemType.PROCEDURE_STEP_EVENT:
									return (
										<ProjectTimelineProcedureStepEventItem
											key={model.uuid}
											procedureStepEvent={model}
										/>
									)
								case ProjectTimelineItemType.PROCEDURE_STEP_REVIEW:
									return (
										<ProjectTimelineProcedureStepReviewItem
											key={model.uuid}
											procedureStepFieldValuesReview={model}
										/>
									)
								case ProjectTimelineItemType.PROJECT_ATTACHMENT:
									return (
										<ProjectTimelineProjectAttachmentItem
											key={model.uuid}
											projectAttachment={model}
										/>
									)
								case ProjectTimelineItemType.PROJECT_COMMENT:
									return <ProjectCommentTimelineItem key={model.uuid} projectComment={model} />
							}
						})
					) : (
						<Card className="flex min-h-32 items-center justify-center" size="sm">
							<Text accentColor="base" size="sm">
								No project timeline items match the current filters.
							</Text>
						</Card>
					)}
				</div>

				{filteredTimelineItems.length > 0 ? (
					<>
						<Separator size="full" />
						<div className="flex h-max justify-between">
							<Text accentColor="base" size="sm">
								Page <Text highContrast>{currentPage + 1}</Text> of{" "}
								<Text highContrast>{pageCount || 1}</Text>
							</Text>
							<ButtonGroup className="flex items-center" size="sm" accentColor="base" variant="ghost">
								<IconButton
									onClick={firstPage}
									aria-label="first-page"
									disabled={pageCount === 0 || currentPage === 0}
								>
									<LuIcon icon={modelIconMapping.chevronsLeft} />
								</IconButton>
								<IconButton
									onClick={prevPage}
									aria-label="previous-page"
									disabled={pageCount === 0 || currentPage === 0}
								>
									<LuIcon icon={modelIconMapping.chevronLeft} />
								</IconButton>
								<IconButton
									onClick={nextPage}
									aria-label="next-page"
									disabled={pageCount === 0 || currentPage === pageCount - 1}
								>
									<LuIcon icon={modelIconMapping.chevronRight} />
								</IconButton>
								<IconButton
									onClick={lastPage}
									aria-label="last-page"
									disabled={pageCount === 0 || currentPage === pageCount - 1}
								>
									<LuIcon icon={modelIconMapping.chevronsRight} />
								</IconButton>
							</ButtonGroup>
						</div>
					</>
				) : null}

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
					<form className="w-full" onSubmit={handleCommentSubmit}>
						<Card className="flex h-max min-h-[80px] w-full flex-col gap-2" size="sm">
							<EditorContext value={editor}>
								<EditorContent editor={editor} className="h-max w-full grow text-sm" />
								<NewLineFloatingMenu editor={editor} />
								<TextBubbleMenu editor={editor} />
							</EditorContext>

							<div className="flex h-max w-full justify-end">
								<IconButton
									aria-label="submit-project-comment"
									type="submit"
									variant="soft"
									accentColor="base"
									size="sm"
								>
									<LuIcon icon={modelIconMapping.arrowUp} />
								</IconButton>
							</div>
						</Card>
					</form>
				</OrganizationAccessRequired>
			</div>
		</section>
	)
})
ProjectTimeline.displayName = "ProjectTimeline"
